import React, {
  FunctionComponent, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import * as globalSelectors from 'global/global-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as userSelectors from 'users/users-selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as networkSelectors from 'data/network/network.selectors';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as fqdnGroupSelectors from 'data/fqdnGroup/fqdnGroup.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as locationSelectors from 'data/location/location.selectors';
import * as locationActions from 'data/location/location.actions';
import { FormTemplate } from 'data/template/template.types';
import { CollectionFunctionFormData } from 'data/collectionFunction/collectionFunction.types';
import { adaptToForm } from 'data/collectionFunction/collectionFunction.adapter';
import ModalDialog from 'shared/modal/dialog';
import { AuditDetails, WithAuditDetails } from 'data/types';
import {
  getAuditDetails,
  getAuditFieldInfo,
  AuditService,
  AuditActionType,
} from 'shared/userAudit/audit-constants';
import CollectionFunctionForm, { CollectionFunctionFormRef } from '../../components/CollectionFunctionForm';
import {
  adaptTemplateOptions,
  adaptTemplateVisibilityMode,
  adaptTemplateTimezoneOptions,
  adaptTemplateInterfaceFields,
  adaptTemplate5GFields,
  adaptTemplateSecinfoidFields,
  adaptTemplateStoreAndForward,
  adaptTemplateWithDataForView,
  adaptLocationField,
  adaptTemplatesBasedOnAppSettings,
} from '../../utils/collectionFunctionTemplate.adapter';
import FieldCurrentState from '../../../../components/FieldCurrentState';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  timezones: userSelectors.getTimezones(state),
  contacts: contactSelectors.getContacts(state),
  cfFtpInfos: cfFtpInfoSelectors.getCfFtpInfoList(state),
  collectionFunctionGroupList: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  collectionFunctionTemplates: templateSelectors.getEnabledCollectionFunctionFormTemplatesByKey(state),
  enabledCollectionFunctions: supportedFeaturesSelectors.getEnabledCollectionFunctions(state),
  selectedCollectionFunction: collectionFunctionSelectors.getSelectedCollectionFunction(state),
  collectionFunction: collectionFunctionSelectors.getCollectionFunction(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  networks: networkSelectors.getNetworks(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  fqdnGroups: fqdnGroupSelectors.getFqdnGroups(state),
  is5GCVcpEnabled: supportedFeaturesSelectors.is5gcVcpEnabled(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  countries: locationSelectors.getCountryList(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
  isT1678HITlsEnabled: discoverySelectors.isT1678HITlsEnabled(state),
  is33108HITlsEnabled: discoverySelectors.is33108HITlsEnabled(state),
});

const mapDispatch = {
  goToCollectionFunctionList: collectionFunctionActions.goToCollectionFunctionList,
  goToCollectionFunctionCopy: collectionFunctionActions.goToCollectionFunctionCopy,
  goToCollectionFunctionEdit: collectionFunctionActions.goToCollectionFunctionEdit,
  fetchCollectionFunction: collectionFunctionActions.fetchCollectionFunctionWithViewOptions,
  deleteCollectionFunction: collectionFunctionActions.deleteCollectionFunction,
  getCfFtpInfoList: cfFtpInfoActions.getCfFtpInfoList,
  fetchLocationList: locationActions.fetchLocationList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const CollectionFunctionView: FunctionComponent<Props> = ({
  deliveryFunctionId,
  history,
  isAuditEnabled,
  userId,
  userName,
  timezones,
  contacts,
  cfFtpInfos,
  collectionFunctionGroupList,
  collectionFunctionTemplates,
  enabledCollectionFunctions,
  selectedCollectionFunction,
  collectionFunction,
  networks,
  securityInfos,
  fqdnGroups,
  is5GCVcpEnabled,
  xcipioNodes,
  countries,
  isLocationRequired,
  isT1678HITlsEnabled,
  is33108HITlsEnabled,
  goToCollectionFunctionList,
  goToCollectionFunctionCopy,
  fetchCollectionFunction,
  deleteCollectionFunction,
  goToCollectionFunctionEdit,
  getCfFtpInfoList,
  fetchLocationList,
}: Props) => {
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [data, setData] = useState<Partial<CollectionFunctionFormData>>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const formRef = useRef<CollectionFunctionFormRef>(null);
  const { t } = useTranslation();

  const collectionFunctionId = selectedCollectionFunction[0]?.id;

  const customFieldComponentMap = {
    collectionFunctionCurrentState: FieldCurrentState,
  };

  useEffect(() => {
    if (collectionFunctionId) {
      fetchCollectionFunction(deliveryFunctionId, collectionFunctionId);
    }
    getCfFtpInfoList(deliveryFunctionId);
    fetchLocationList();
  }, [
    collectionFunctionId,
    deliveryFunctionId,
    fetchCollectionFunction,
    getCfFtpInfoList,
    fetchLocationList,
  ]);

  useEffect(() => {
    let availableTemplates = adaptTemplateOptions(
      collectionFunctionTemplates,
      timezones,
      contacts,
      collectionFunctionGroupList,
      cfFtpInfos,
      networks,
      securityInfos,
      fqdnGroups,
      enabledCollectionFunctions,
      xcipioNodes,
      countries,
    );

    availableTemplates = adaptTemplatesBasedOnAppSettings(
      availableTemplates,
      isLocationRequired,
      isT1678HITlsEnabled,
      is33108HITlsEnabled,
    )

    setTemplates(availableTemplates);
  }, [
    collectionFunctionTemplates,
    timezones,
    contacts,
    cfFtpInfos,
    collectionFunctionGroupList,
    enabledCollectionFunctions,
    networks,
    securityInfos,
    fqdnGroups,
    xcipioNodes,
    countries,
  ]);

  useEffect(() => {
    if (collectionFunction != null && templates != null) {
      const formData = adaptToForm(collectionFunction);
      let templateData = adaptTemplateVisibilityMode(templates[formData.tag], 'view');
      templateData = adaptTemplateTimezoneOptions(templateData, formData, timezones);
      templateData = adaptTemplateInterfaceFields(templateData, formData);
      templateData = adaptTemplate5GFields(templateData, is5GCVcpEnabled);
      templateData = adaptTemplateSecinfoidFields(templateData, isT1678HITlsEnabled, is33108HITlsEnabled);
      templateData = adaptTemplateWithDataForView(formData, templateData);

      if (data) {
        const enable = data.ftpId !== '';
        templateData = adaptTemplateStoreAndForward(templateData, enable, formData.tag);
      }

      if (formData.stateName) {
        templateData = adaptLocationField(templateData, [formData.stateName], 'stateName');
      }
      if (formData.city) {
        templateData = adaptLocationField(templateData, [formData.city], 'city');
      }

      setData(formData);
      setTemplate(templateData);
    }
  }, [collectionFunction, templates, timezones, is5GCVcpEnabled]);

  const handleCancel = () => {
    goToCollectionFunctionList(history);
  };

  const handleClickCopy = () => {
    goToCollectionFunctionCopy(history);
  };

  const handleClickCEdit = () => {
    goToCollectionFunctionEdit(history);
  };

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDeleteCollectionFunction = () => {

    if (collectionFunction?.id == null) {
      return;
    }

    const collectionFunctionBody: WithAuditDetails<void> = { data: undefined };

    setIsDeleteDialogOpen(false);

    deleteCollectionFunction(deliveryFunctionId, collectionFunction.id, collectionFunction.name, collectionFunctionBody, history);

  };

  if (!template || !data) {
    return null;
  }

  return (
    <div className="app-container-content" data-testid="CollectionFunctionView">
      <CollectionFunctionForm
        ref={formRef}
        title={t('collectionFunction.view.title')}
        isEditing={false}
        data={data}
        template={template}
        customFieldComponentMap={customFieldComponentMap}
        onClickCancelClose={handleCancel}
        onClickDelete={handleOpenDeleteDialog}
        onClickCopy={handleClickCopy}
        onClickEdit={handleClickCEdit}
      />
      <ModalDialog
        width="500px"
        isOpen={isDeleteDialogOpen}
        title={t('collectionFunction.deletePopup.title')}
        displayMessage={t('collectionFunction.deletePopup.message', { name: collectionFunction?.name })}
        actionText={t('collectionFunction.deletePopup.action')}
        onSubmit={handleConfirmDeleteCollectionFunction}
        onClose={handleCloseDeleteDialog}
      />
    </div>
  );
};

export default withRouter(connector(CollectionFunctionView));
